import React, { useCallback, useEffect, useRef } from "react";
import { ValidatedTextInputField } from "../../components/fields/validatedTextInputField";
import { useTranslationContext } from "../../context";
import { ProvetValidationUtilities } from "../../utilities";
import { removeEmoji } from "./helpers/contactDetailsUtility";
import { useContactDetailsValidation } from "./hooks/useContactDetailsValidation";

/**
 * Props for CustomerInfoSection component.
 * @property validationHook - The validation hook instance for managing form state and validation.
 */
interface CustomerInfoSectionProps {
  language: string;
  /** Validation hook instance for managing form state and validation */
  validationHook: ReturnType<typeof useContactDetailsValidation>;
}

/**
 * CustomerInfoSection renders the customer information fields for the Provet contact details form.
 * Handles validation, error and warning display, and state updates for each field.
 */
const CustomerInfoSection = ({ language, validationHook }: CustomerInfoSectionProps) => {
  const translationContext = useTranslationContext();
  const {
    client: state,
    clientValidation: validation,
    formValidated,
    identificationNumberRef,
    setClient,
    setClientValidation: setValidation,
    blurred,
    setBlurred,
    validateField
  } = validationHook;

  // Create refs to access latest state without triggering re-renders
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Identification Number handlers
  const handleIdentificationNumberChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setClient(client => ({ ...client, identificationNumber: input }));
      setValidation(prev => ({
        ...prev,
        identificationNumberValid:
          ProvetValidationUtilities.isValidIdentificationNumber(value)
      }));
    },
    [setClient, setValidation]
  );

  const handleIdentificationNumberBlur = useCallback(() => {
    setBlurred(prev => ({ ...prev, identificationNumber: true }));
    // Emoji are removed once the customer leaves the field
    setClient(current => ({
      ...current,
      identificationNumber: removeEmoji(current.identificationNumber ?? "")
    }));
    const identificationNumber = removeEmoji(stateRef.current.identificationNumber ?? "");
    if (identificationNumber && identificationNumber.trim()) {
      setValidation(prev => ({
        ...prev,
        identificationNumberWarning:
          !ProvetValidationUtilities.isValidSwedishIdentificationNumber(
            identificationNumber
          )
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        identificationNumberWarning: false
      }));
    }
  }, [setBlurred, setValidation, setClient]);

  // First Name handlers
  const handleFirstNameChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setClient(client => ({ ...client, firstName: input }));
      setValidation(prev => ({
        ...prev,
        firstNameValid: ProvetValidationUtilities.isValidClientFirstName(value)
      }));
    },
    [setClient, setValidation]
  );

  const handleFirstNameBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, firstName: true }));
    // Emoji are removed once the customer leaves the field
    setClient(current => ({
      ...current,
      firstName: removeEmoji(current.firstName ?? "")
    }));
    const valid = ProvetValidationUtilities.isValidClientFirstName(
      removeEmoji(stateRef.current.firstName ?? "")
    );
    await validateField(valid, setValidation, "firstNameValid", "#first-name");
  }, [setBlurred, validateField, setValidation, setClient]);

  // Last Name handlers
  const handleLastNameChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setClient(client => ({ ...client, lastName: input }));
      setValidation(prev => ({
        ...prev,
        lastNameValid: ProvetValidationUtilities.isValidClientLastName(value)
      }));
    },
    [setClient, setValidation]
  );

  const handleLastNameBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, lastName: true }));
    // Emoji are removed once the customer leaves the field
    setClient(current => ({ ...current, lastName: removeEmoji(current.lastName ?? "") }));
    const valid = ProvetValidationUtilities.isValidClientLastName(
      removeEmoji(stateRef.current.lastName ?? "")
    );
    await validateField(valid, setValidation, "lastNameValid", "#last-name");
  }, [setBlurred, validateField, setValidation, setClient]);

  // Street Address handler
  const handleStreetAddressChange = useCallback(
    (value: string) => {
      setClient(client => ({ ...client, streetAddress: value }));
    },
    [setClient]
  );

  // Zip Code handlers
  const handleZipCodeChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setClient(client => ({ ...client, zipCode: input }));
      setValidation(prev => ({
        ...prev,
        zipCodeValid: ProvetValidationUtilities.isValidZipCode(value)
      }));
    },
    [setClient, setValidation]
  );

  const handleZipCodeBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, zipCode: true }));
    // Emoji are removed once the customer leaves the field
    setClient(current => ({ ...current, zipCode: removeEmoji(current.zipCode ?? "") }));
    const valid = ProvetValidationUtilities.isValidZipCode(
      removeEmoji(stateRef.current.zipCode ?? "")
    );
    await validateField(valid, setValidation, "zipCodeValid", "#zip-code");
  }, [setBlurred, validateField, setValidation, setClient]);

  // City handler
  const handleCityChange = useCallback(
    (value: string) => {
      setClient(client => ({ ...client, city: value }));
    },
    [setClient]
  );

  return (
    <div className="flex flex-col gap-lg">
      {/* Identification Number Field */}
      <ValidatedTextInputField
        id="identification-number"
        label={translationContext?.translations?.identificationNumber}
        description={translationContext?.translations?.identificationNumberDescription}
        errorMessage={translationContext?.translations?.identificationNumberError}
        warningMessage={translationContext?.translations?.identificationNumberWarning}
        placeholder={translationContext?.translations?.identificationNumberPlaceholder}
        value={state.identificationNumber}
        required={true}
        isValid={validation.identificationNumberValid}
        hasWarning={validation.identificationNumberWarning}
        hasBlurred={blurred.identificationNumber}
        formValidated={formValidated}
        forwardedRef={identificationNumberRef}
        onValueChange={handleIdentificationNumberChange}
        onBlur={handleIdentificationNumberBlur}
      />

      {/* First Name Field */}
      <ValidatedTextInputField
        id="first-name"
        label={translationContext?.translations?.firstName}
        description={translationContext?.translations?.firstNameDescription}
        errorMessage={translationContext?.translations?.firstNameError}
        placeholder={translationContext?.translations?.firstNamePlaceholder}
        value={state.firstName}
        required={true}
        isValid={validation.firstNameValid}
        hasBlurred={blurred.firstName}
        formValidated={formValidated}
        onValueChange={handleFirstNameChange}
        onBlur={handleFirstNameBlur}
      />

      {/* Last Name Field */}
      <ValidatedTextInputField
        id="last-name"
        label={translationContext?.translations?.lastName}
        description={translationContext?.translations?.lastNameDescription}
        errorMessage={translationContext?.translations?.lastNameError}
        placeholder={translationContext?.translations?.lastNamePlaceholder}
        value={state.lastName}
        required={true}
        isValid={validation.lastNameValid}
        hasBlurred={blurred.lastName}
        formValidated={formValidated}
        onValueChange={handleLastNameChange}
        onBlur={handleLastNameBlur}
      />

      {/* Street Address Field */}
      <ValidatedTextInputField
        id="street-address"
        label={translationContext?.translations?.streetAddress}
        description={translationContext?.translations?.streetAddressDescription}
        placeholder={translationContext?.translations?.streetAddressPlaceholder}
        value={state.streetAddress}
        required={false}
        isValid={true}
        hasBlurred={false}
        formValidated={formValidated}
        onValueChange={handleStreetAddressChange}
      />

      {/* Zip Code Field */}
      <ValidatedTextInputField
        id="zip-code"
        label={translationContext?.translations?.zipCode}
        description={translationContext?.translations?.zipCodeDescription}
        errorMessage={translationContext?.translations?.zipCodeError}
        placeholder={translationContext?.translations?.zipCodePlaceholder}
        value={state.zipCode}
        required={false}
        isValid={validation.zipCodeValid}
        hasBlurred={blurred.zipCode}
        formValidated={formValidated}
        onValueChange={handleZipCodeChange}
        onBlur={handleZipCodeBlur}
      />

      {/* City Field */}
      <ValidatedTextInputField
        id="city"
        label={translationContext?.translations?.city}
        description={translationContext?.translations?.cityDescription}
        placeholder={translationContext?.translations?.cityPlaceholder}
        value={state.city}
        required={false}
        isValid={true}
        hasBlurred={false}
        formValidated={formValidated}
        onValueChange={handleCityChange}
      />

      {/* Email field removed - now collected in phone verification step */}

      {/* Honeypot field for bot detection - hidden from real users */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          opacity: 0,
          height: 0,
          overflow: "hidden"
        }}
      >
        {/* Deliberately not named or labelled like a contact field (it used to be "website"),
            so browser autofill and password managers leave it empty for real customers */}
        <label htmlFor="provet-extra-field">Leave this field empty</label>
        <input
          type="text"
          id="provet-extra-field"
          name="provet-extra-field"
          tabIndex={-1}
          autoComplete="off"
          value={state.extraField || ""}
          onChange={e => setClient(prev => ({ ...prev, extraField: e.target.value }))}
        />
      </div>
    </div>
  );
};

export default CustomerInfoSection;
